import React, { useEffect, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { GoogleLogin } from '@react-oauth/google';
import jwt from 'jsonwebtoken';
import styled from '@emotion/styled';
import { useTheme } from 'emotion-theming';

import Button from '../../../../shared/_TBComponents/Button';
import Text from '../../../../shared/_TBComponents/Text';
import {
  setGoogleError,
  setGoogleUser,
  setRegisteringWithGoogle,
} from '../../ducks/registrationActions';

const ConnectedFormGroup = styled('div')`
  align-items: center;
  display: flex;
  flex-direction: row;
  justify-content: center;
`;

const CurrentlyRegisteringContainer = styled('div')`
  align-items: center;
  align-self: center;
  background-color: #EFF6FC;
  border-radius: 24px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-bottom: 25px;
  padding: 15px;
  text-align: center;
  width: 400px;
`;

const FormGroup = styled('div')`
  display: flex;
  justify-content: center;
  margin-bottom: 15px;
`;

const GoogleAuthSection = ({
  onGoogleConnect,
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();

  const [googleLoginWidth, setGoogleLoginWidth] = useState('400');

  const isRegisteringWithGoogle = useSelector(
    (state) => state.registration.data.isRegisteringWithGoogle,
  );

  useEffect(() => {
    const handleResize = () => {
      setGoogleLoginWidth(window.innerWidth < 425 ? '250' : '400');
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleGoogleSuccess = async (credentialResponse) => {
    // GoogleLogin component returns credentialResponse.credential which is the id_token
    const idToken = credentialResponse.credential;
    if (!idToken) {
      return;
    }

    const googleObj = {
      tokenId: idToken,
    };

    // Decode JWT payload to get user info
    try {
      const decodedToken = jwt.decode(idToken);

      if (decodedToken) {
        googleObj.email = decodedToken.email;
        googleObj.givenName = decodedToken.given_name;
        googleObj.familyName = decodedToken.family_name;
      }
    } catch (error) {
      // Do nothing, if it failed user can just manually enter their info
    }

    batch(() => {
      dispatch(setRegisteringWithGoogle(true));
      dispatch(setGoogleError(false));
      dispatch(setGoogleUser(googleObj));
    });

    if (onGoogleConnect) {
      onGoogleConnect({
        tokenId: idToken,
      });
    }
  };

  const handleGoogleError = () => {
    dispatch(setGoogleError(true));
  };

  const handleGoogleDisconnect = () => {
    batch(() => {
      dispatch(setRegisteringWithGoogle(false));
      dispatch(setGoogleError(false));
      dispatch(setGoogleUser({}));
    });
  };

  if (isRegisteringWithGoogle) {
    return (
      <ConnectedFormGroup>
        <CurrentlyRegisteringContainer>
          <Text styledTextProps={{ fontWeight: '700' }}>
            Connected with Google Sign On
          </Text>
          <Text styledWrapperProps={{ marginBottom: '10px' }}>
            You will be able to sign in with your Google account.
            We just need a few additional details to complete your registration.
          </Text>
          <Button
            bgColor={theme.colors.text}
            fontSize='14px'
            name='Disconnect'
            noBorder
            onClick={handleGoogleDisconnect}
            rounded
            txtColor={theme.colors.white}
            type='button'
            upperCase
          />
        </CurrentlyRegisteringContainer>
      </ConnectedFormGroup>
    );
  }

  return (
    <FormGroup>
      <GoogleLogin
        locale='en'
        onError={handleGoogleError}
        onSuccess={handleGoogleSuccess}
        text='signup_with'
        width={googleLoginWidth}
      />
    </FormGroup>
  );
};

GoogleAuthSection.propTypes = {
  onGoogleConnect: PropTypes.func,
};

GoogleAuthSection.defaultProps = {
  onGoogleConnect: null,
};

export default GoogleAuthSection;
